import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
// @mui
import {
  Grid,
  Stack,
  Button,
  Drawer,
  Divider,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  FormHelperText,
  CircularProgress,
  TextField,
  IconButton,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

// components
import {
  Close,
  UploadFile,
  ExpandMore,
  People,
  PersonAdd,
  DevicesOther,
  Link as LinkIcon,
  ArrowBack,
  ArrowForward,
  Download,
  ContentCopy,
  CheckCircle,
  PhoneAndroid,
  Subscriptions,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DataGrid, GridToolbarExport } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { useGetOrganizationsQuery } from "../../../redux/services/organization.service";
import {
  useBulkLinkUserDeviceMutation,
  useCheckBulkDeviceSubscriptionMutation,
} from "../../../redux/services/subscription.service";
import { useGetEventPlacesQuery } from "../../../redux/services/device.service";
import Scrollbar from "../../../components/scrollbar";
import CustomDialog from "../../../components/dialog/CustomDialog";
import { DeviceSubscriptionSchema } from "../../../forms-schema/form.schema";

DeviceSubscriptionForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

const supportedFileTypes = [
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const STEPS = ["Upload & Check", "Configure & Submit"];

export default function DeviceSubscriptionForm({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedEventPlace, setSelectedEventPlace] = useState("");
  const [filteredTags, setFilteredTags] = useState([]);

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(DeviceSubscriptionSchema),
  });

  // File and dialog states
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [duplicateContacts, setDuplicateContacts] = useState([]);
  const [openResultsDialog, setOpenResultsDialog] = useState(false);
  const [subscriptionResults, setSubscriptionResults] = useState({
    newUsers: [],
    usersWithIsCodeVerifiedFalse: [],
    usersWithIsCodeVerifiedTrue: [],
    totalUsers: [],
    s3Url: "",
  });

  // Check bulk device subscription states
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [checkS3Url, setCheckS3Url] = useState("");

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (!open) {
      reset({
        senderOrganizationId: "",
        recipientOrganizationId: "",
        eventPlace: "",
        tag: "",
        duration: "",
      });
      setSelectedFile(null);
      setFileError("");
      setSelectedEventPlace("");
      setFilteredTags([]);
      setActiveStep(0);
      // Reset check states
      setCheckResult(null);
      setCheckS3Url("");
    }
  }, [open, reset]);

  const {
    isLoading: isOrgLoading,
    isFetching: isOrgFetching,
    data: organizations,
  } = useGetOrganizationsQuery(null, {
    skip: !open,
  });

  const {
    data: eventPlaces,
    isSuccess: isSuccessEventPlaces,
    isLoading: isLoadingEventPlaces,
  } = useGetEventPlacesQuery(
    {
      organizationId: watch("recipientOrganizationId"),
    },
    {
      skip: !open || !watch("recipientOrganizationId"),
    },
  );

  const [bulkLinkUserDevice] = useBulkLinkUserDeviceMutation();
  const [checkBulkDeviceSubscription] =
    useCheckBulkDeviceSubscriptionMutation();

  // Check bulk device subscription handler (Phase 1)
  const handleCheckSubscription = async (values) => {
    if (!selectedFile) {
      setFileError("Please select a file first");
      return;
    }

    if (!values.senderOrganizationId || !values.recipientOrganizationId) {
      enqueueSnackbar("Please select both sender and recipient organizations", {
        variant: "warning",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
      return;
    }

    setCheckLoading(true);
    try {
      const payload = {
        file: selectedFile,
        senderOrganizationId: values.senderOrganizationId,
        recipientOrganizationId: values.recipientOrganizationId,
        ...(values.eventPlace && { eventPlace: values.eventPlace }),
        ...(values.tag && { tag: values.tag }),
      };

      const result = await checkBulkDeviceSubscription(payload);

      if (result?.data?.success) {
        const responseData = result.data.data;
        setCheckResult(responseData);
        setCheckS3Url(responseData?.s3Url || "");
        enqueueSnackbar(
          "File checked successfully! Review the results below.",
          {
            variant: "success",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          },
        );
      } else if (result?.error) {
        const errorData = result?.error?.data;
        enqueueSnackbar(errorData?.message || "Failed to check file", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
      }
    } catch (error) {
      console.error("Error checking device subscription:", error);
      enqueueSnackbar("An error occurred. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
    } finally {
      setCheckLoading(false);
    }
  };

  // Submit handler for Phase 2
  const handleClick = async (values) => {
    setLoading(true);
    try {
      const payload = {
        file: selectedFile,
        senderOrganizationId: values.senderOrganizationId,
        recipientOrganizationId: values.recipientOrganizationId,
        ...(values.eventPlace && { eventPlace: values.eventPlace }),
        ...(values.tag && { tag: values.tag }),
        ...(values.duration && { duration: values.duration }),
      };

      const result = await bulkLinkUserDevice(payload);

      if (result?.data?.success) {
        enqueueSnackbar(result?.data?.message || "Users linked successfully", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });

        // Show results dialog with subscription details
        const responseData = result?.data?.data;
        setSubscriptionResults({
          newUsers: responseData?.newUsers || [],
          usersWithIsCodeVerifiedFalse:
            responseData?.usersWithIsCodeVerifiedFalse || [],
          usersWithIsCodeVerifiedTrue:
            responseData?.usersWithIsCodeVerifiedTrue || [],
          totalUsers: responseData?.totalUsers || [],
          s3Url: responseData?.s3Url || "",
        });
        setOpenResultsDialog(true);

        reset();
        setSelectedFile(null);
        setOpenConfirmationDialog(false);
      } else if (result?.error) {
        const errorData = result?.error?.data;

        // Handle duplicate contact numbers
        if (
          errorData?.statusCode === 400 &&
          errorData?.message?.includes("Duplicate")
        ) {
          setDuplicateContacts(errorData?.data || []);
          setOpenDuplicateDialog(true);
          setOpenConfirmationDialog(false);
          enqueueSnackbar(
            errorData?.message || "Duplicate contact numbers found",
            {
              variant: "warning",
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
            },
          );
        } else {
          enqueueSnackbar(errorData?.message || "Something went wrong", {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          });
          setOpenConfirmationDialog(false);
        }
      }
    } catch (error) {
      console.error("Error occurred during bulk link user device:", error);
      enqueueSnackbar("An error occurred. Please try again later.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
    } finally {
      setLoading(false);
    }
  };

  const fileRef = useRef();

  const handleFileChange = (e) => {
    try {
      const file = e.target.files[0];

      if (!file) {
        enqueueSnackbar("No file selected", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setSelectedFile(null);
        enqueueSnackbar("File size exceeds 10MB limit", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
        return;
      }

      if (!supportedFileTypes.includes(file.type)) {
        setSelectedFile(null);
        enqueueSnackbar(
          "Unsupported file format. Please upload .xlsx or .xls file",
          {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          },
        );
        return;
      }

      setFileError("");
      setSelectedFile(file);
      setCheckResult(null);
      setCheckS3Url("");
    } catch (error) {
      console.error("Error while handling file change:", error);
      enqueueSnackbar("An error occurred while processing the file.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    try {
      const file = e.dataTransfer.files[0];

      if (!file) {
        enqueueSnackbar("No file selected", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setSelectedFile(null);
        enqueueSnackbar("File size exceeds 10MB limit", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        });
        return;
      }

      if (!supportedFileTypes.includes(file.type)) {
        setSelectedFile(null);
        enqueueSnackbar(
          "Unsupported file format. Please upload .xlsx or .xls file",
          {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
          },
        );
        return;
      }

      setFileError("");
      setSelectedFile(file);
      setCheckResult(null);
      setCheckS3Url("");
    } catch (error) {
      console.error("Error while handling file drop:", error);
      enqueueSnackbar("An error occurred while processing the file.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
    }
  };

  const handleOpenConfirmationDialog = () => {
    if (!selectedFile) {
      setFileError("Please select a file");
      return;
    }
    setOpenConfirmationDialog(true);
  };

  const handleEventPlaceChange = (event) => {
    try {
      const selectedPlace = event.target.value;
      setSelectedEventPlace(selectedPlace);
      setValue("eventPlace", selectedPlace);

      const tags =
        eventPlaces?.data?.allEventPlaces?.filter(
          (eventItem) =>
            eventItem.eventPlace === selectedPlace && eventItem.tag !== null,
        )[0]?.tags || [];

      setFilteredTags(tags);
      setValue("tag", "");
    } catch (error) {
      console.error("Error while handling event place change:", error);
    }
  };

  const handleNextStep = () => {
    if (activeStep === 0 && !checkResult) {
      enqueueSnackbar("Please check the file first before proceeding", {
        variant: "warning",
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      });
      return;
    }
    setActiveStep(1);
  };

  const handleBackStep = () => {
    setActiveStep(0);
  };

  // Render Phase 1: Upload & Check
  const renderPhase1 = () => (
    <Stack spacing={3}>
      <Alert severity="info" sx={{ py: 1 }}>
        <Typography variant="body2">
          <strong>Step 1:</strong> Select organizations, upload Excel file, and
          check device availability before proceeding.
        </Typography>
      </Alert>

      {/* Sender Organization Select */}
      <FormControl fullWidth>
        <InputLabel
          id="sender-org-label"
          error={Boolean(errors.senderOrganizationId)}
        >
          Sender Organization *
        </InputLabel>
        <Select
          {...register("senderOrganizationId", { required: true })}
          labelId="sender-org-label"
          id="sender-org-select"
          label="Sender Organization *"
          inputProps={{ "data-testid": "senderOrganizationId" }}
          error={Boolean(errors.senderOrganizationId)}
          value={watch("senderOrganizationId") || ""}
        >
          {isOrgLoading || isOrgFetching ? (
            <MenuItem sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            organizations?.data
              ?.toSorted((a, b) => a?.name?.localeCompare(b?.name))
              ?.map((org) => (
                <MenuItem key={org?.id} value={org?.id}>
                  {org?.name}
                </MenuItem>
              ))
          )}
        </Select>
        <FormHelperText error={Boolean(errors.senderOrganizationId)}>
          {errors.senderOrganizationId?.message}
        </FormHelperText>
      </FormControl>

      {/* Recipient Organization Select */}
      <FormControl fullWidth>
        <InputLabel
          id="recipient-org-label"
          error={Boolean(errors.recipientOrganizationId)}
        >
          Recipient Organization *
        </InputLabel>
        <Select
          {...register("recipientOrganizationId", { required: true })}
          labelId="recipient-org-label"
          id="recipient-org-select"
          label="Recipient Organization *"
          inputProps={{ "data-testid": "recipientOrganizationId" }}
          error={Boolean(errors.recipientOrganizationId)}
          value={watch("recipientOrganizationId") || ""}
        >
          {isOrgLoading || isOrgFetching ? (
            <MenuItem sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            organizations?.data
              ?.toSorted((a, b) => a?.name?.localeCompare(b?.name))
              ?.map((org) => (
                <MenuItem key={org?.id} value={org?.id}>
                  {org?.name}
                </MenuItem>
              ))
          )}
        </Select>
        <FormHelperText error={Boolean(errors.recipientOrganizationId)}>
          {errors.recipientOrganizationId?.message}
        </FormHelperText>
      </FormControl>

      {/* Event Place */}
      <FormControl fullWidth>
        <InputLabel id="eventPlace-label">Event Place</InputLabel>
        <Select
          labelId="eventPlace-label"
          id="eventPlace-select"
          label="Event Place"
          value={selectedEventPlace}
          onChange={handleEventPlaceChange}
          inputProps={{ "data-testid": "eventPlace" }}
          disabled={!watch("recipientOrganizationId")}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {isLoadingEventPlaces && (
            <MenuItem>
              <CircularProgress size={24} />
            </MenuItem>
          )}
          {isSuccessEventPlaces &&
            eventPlaces?.data?.allEventPlaces
              ?.filter((eventPlace) => eventPlace?.eventPlace && typeof eventPlace.eventPlace === "string" && eventPlace.eventPlace.trim() !== "")
              ?.map((eventPlace, index) => (
                <MenuItem
                  key={`${eventPlace.eventPlace}-${index}`}
                  value={eventPlace.eventPlace}
                >
                  {eventPlace.eventPlace}
                </MenuItem>
              ))}
        </Select>
      </FormControl>

      {/* Tag */}
      <FormControl fullWidth>
        <InputLabel id="tag-label">Tag</InputLabel>
        <Select
          labelId="tag-label"
          id="tag-select"
          {...register("tag")}
          label="Tag"
          value={watch("tag") ?? ""}
          disabled={filteredTags.length === 0}
          inputProps={{ "data-testid": "tag" }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {filteredTags.length > 0 ? (
            filteredTags.map((tag, index) => (
              <MenuItem key={index} value={tag}>
                {tag}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No tags available</MenuItem>
          )}
        </Select>
      </FormControl>

      {/* File Upload */}
      <Box>
        <InputLabel htmlFor="file" sx={{ mb: 1 }}>
          Excel File *
        </InputLabel>

        <input
          onChange={handleFileChange}
          ref={fileRef}
          type="file"
          accept=".xlsx, .xls"
          hidden
          data-testid="file-input"
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px dashed #D9D9D9",
            borderRadius: "10px",
            padding: "20px",
            justifyContent: "center",
            flexDirection: "column",
            cursor: "pointer",
            height: 180,
            "&:hover": { backgroundColor: "#F5F5F5" },
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <UploadFile sx={{ fontSize: "50px", color: "#D9D9D9" }} />
          <Typography
            variant="body2"
            sx={{ marginTop: 0.5, textAlign: "center", fontStyle: "italic" }}
          >
            {selectedFile ? selectedFile.name : "(No file selected)"}
          </Typography>
          <Button
            sx={{ marginTop: 2, boxShadow: "none" }}
            variant="outlined"
            onClick={() => fileRef.current.click()}
          >
            Browse
          </Button>
          <Typography
            variant="body2"
            sx={{ marginTop: 0.5, textAlign: "center" }}
          >
            Or drag here{" "}
            <Typography
              component="span"
              variant="body2"
              sx={{ fontStyle: "italic" }}
            >
              (.xlsx, .xls - max 10MB)
            </Typography>
          </Typography>
        </Box>
        {fileError && (
          <FormHelperText error={Boolean(fileError)}>
            {fileError}
          </FormHelperText>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          Required headers: First Name, Last Name, Contact Number, Email, Date
          of Birth, Gender, City, State, Country, Pincode
        </Typography>

        <a
          href="https://sunbots-people.s3.ap-south-1.amazonaws.com/1724828563100_1710311771521_SmartOn%20User%20Form.xlsx"
          style={{
            textTransform: "none",
            textDecoration: "underline",
            color: "#1976d2",
            cursor: "pointer",
            marginTop: "8px",
            display: "inline-block",
          }}
          rel="noreferrer"
          target="_blank"
        >
          Download Template
        </a>
      </Box>

      {/* Check Button */}
      <LoadingButton
        variant="contained"
        onClick={handleSubmit(handleCheckSubscription)}
        loading={checkLoading}
        disabled={!selectedFile}
        fullWidth
        size="large"
      >
        Check Device Availability
      </LoadingButton>

      {/* Check Results */}
      {checkResult && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />

          {/* Download Excel Button */}
          {checkS3Url && (
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => window.open(checkS3Url, "_blank")}
              fullWidth
              sx={{ mb: 2 }}
            >
              Download Detailed Report (Excel)
            </Button>
          )}

          {/* Summary Cards */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            📊 Summary
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Paper
                sx={{ p: 1.5, textAlign: "center", bgcolor: "primary.50" }}
              >
                <People color="primary" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.summary?.totalUsersInExcel || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total in Excel
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper sx={{ p: 1.5, textAlign: "center", bgcolor: "info.50" }}>
                <CheckCircle color="info" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.summary?.uniqueContactNumbers || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Unique Contacts
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper
                sx={{ p: 1.5, textAlign: "center", bgcolor: "warning.50" }}
              >
                <ContentCopy color="warning" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.summary?.duplicateContactNumbers || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Duplicates
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper
                sx={{ p: 1.5, textAlign: "center", bgcolor: "success.50" }}
              >
                <PersonAdd color="success" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.summary?.newUsers || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  New Users
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper
                sx={{ p: 1.5, textAlign: "center", bgcolor: "secondary.50" }}
              >
                <DevicesOther color="secondary" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.summary?.usersFoundInDatabase || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Found in DB
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6}>
              <Paper sx={{ p: 1.5, textAlign: "center", bgcolor: "error.50" }}>
                <People color="error" sx={{ fontSize: 24 }} />
                <Typography variant="h6" fontWeight="bold">
                  {checkResult.summary?.usersNotFoundInDatabase || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Not in DB
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Additional Stats */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2">
                View Additional Statistics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Paper
                    sx={{ p: 1.5, textAlign: "center", bgcolor: "grey.50" }}
                  >
                    <Subscriptions color="primary" sx={{ fontSize: 20 }} />
                    <Typography variant="h6" fontWeight="bold">
                      {checkResult.summary?.usersWithSubscriptionAndDevice || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sub + Device
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    sx={{ p: 1.5, textAlign: "center", bgcolor: "grey.50" }}
                  >
                    <PhoneAndroid color="secondary" sx={{ fontSize: 20 }} />
                    <Typography variant="h6" fontWeight="bold">
                      {checkResult.summary?.usersWithDeviceOnly || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Device Only
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    sx={{ p: 1.5, textAlign: "center", bgcolor: "grey.50" }}
                  >
                    <Subscriptions color="info" sx={{ fontSize: 20 }} />
                    <Typography variant="h6" fontWeight="bold">
                      {checkResult.summary?.usersWithSubscriptionOnly || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sub Only
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    sx={{ p: 1.5, textAlign: "center", bgcolor: "grey.50" }}
                  >
                    <LinkIcon color="warning" sx={{ fontSize: 20 }} />
                    <Typography variant="h6" fontWeight="bold">
                      {checkResult.summary?.duplicateUsers || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Duplicate Users
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Warnings */}
          {checkResult.summary?.duplicateContactNumbers > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{checkResult.summary.duplicateContactNumbers}</strong>{" "}
                duplicate contact numbers found in the Excel file.
              </Typography>
            </Alert>
          )}

          {checkResult.summary?.usersNotFoundInDatabase > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{checkResult.summary.usersNotFoundInDatabase}</strong>{" "}
                users not found in database (will be created as new users).
              </Typography>
            </Alert>
          )}

          {checkResult.summary?.usersWithSubscriptionAndDevice > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>
                  {checkResult.summary.usersWithSubscriptionAndDevice}
                </strong>{" "}
                users already have subscription and device linked.
              </Typography>
            </Alert>
          )}

          {/* Next Button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleNextStep}
            endIcon={<ArrowForward />}
            sx={{ mt: 3 }}
          >
            Proceed to Step 2
          </Button>
        </Box>
      )}
    </Stack>
  );

  // Render Phase 2: Configure & Submit
  const renderPhase2 = () => (
    <Stack spacing={3}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={handleBackStep}
        sx={{ alignSelf: "flex-start" }}
      >
        Back to Step 1
      </Button>

      <Alert severity="info" sx={{ py: 1 }}>
        <Typography variant="body2">
          <strong>Step 2:</strong> Review and configure device subscription for{" "}
          <strong>{selectedFile?.name}</strong>
        </Typography>
      </Alert>

      {/* File & Organization Info */}
      <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={2} alignItems="center">
            <UploadFile color="primary" />
            <Box>
              <Typography variant="subtitle2">{selectedFile?.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {checkResult?.summary?.totalUsersInExcel || 0} users •{" "}
                {checkResult?.summary?.uniqueContactNumbers || 0} unique •{" "}
                {checkResult?.summary?.newUsers || 0} new
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {/* Duration */}
      <TextField
        {...register("duration")}
        label="Duration (in days)"
        type="number"
        fullWidth
        error={Boolean(errors.duration)}
        helperText={
          errors.duration?.message ||
          "Optional - defaults to device plan duration if not specified"
        }
        inputProps={{ min: 1 }}
      />

      {/* Warning */}
      <Alert severity="warning" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Note:</strong> Users with active subscriptions or already
          linked to devices will be skipped.
        </Typography>
      </Alert>

      {/* Submit Button */}
      <LoadingButton
        variant="contained"
        sx={{ mt: 2 }}
        onClick={handleSubmit(handleOpenConfirmationDialog)}
        loading={loading}
        size="large"
        fullWidth
      >
        Submit
      </LoadingButton>
    </Stack>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 450 },
            border: "none",
            overflow: "hidden",
          },
        }}
      >
        <Scrollbar>
          <Stack spacing={3} sx={{ p: 3 }}>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">Link Devices</Typography>
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </Box>
            <Divider />

            {/* Stepper */}
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Phase Content */}
            {activeStep === 0 ? renderPhase1() : renderPhase2()}
          </Stack>
        </Scrollbar>
      </Drawer>

      {/* Confirmation Dialog */}
      <CustomDialog
        open={openConfirmationDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        title="Confirm Link Devices"
        description={`Are you sure you want to proceed with device subscription for ${
          checkResult?.summary?.canLink || 0
        } users from "${selectedFile?.name}"?`}
        onConfirm={handleSubmit(handleClick)}
        isLoading={loading}
      />

      {/* Duplicate Contact Numbers Dialog */}
      <CustomDialog
        open={openDuplicateDialog}
        title="Duplicate Contact Numbers Found"
        description={
          <>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", marginBottom: 2 }}
            >
              The following duplicate contact numbers were found in the Excel
              file. Please remove duplicates and try again.
            </Typography>
            {duplicateContacts?.length > 0 && (
              <DataGrid
                columns={[
                  {
                    field: "contactNumber",
                    headerName: "Contact Number",
                    width: 200,
                  },
                ]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                rows={duplicateContacts.map((contact, index) => ({
                  id: index,
                  contactNumber: contact,
                }))}
                sx={{ width: "100%" }}
                getRowId={(row) => row?.id}
                disableRowSelectionOnClick
                filterMode="client"
                pagination
                pageSizeOptions={[10, 25, 50, 100]}
                paginationMode="client"
                sortingMode="client"
                scrollbarSize={1}
                autoHeight
                slots={{ toolbar: GridToolbarExport }}
              />
            )}
          </>
        }
        cancelButton={false}
        confirmText="Close"
        onConfirm={() => {
          setOpenDuplicateDialog(false);
          setDuplicateContacts([]);
        }}
        isLoading={loading}
      />

      {/* Subscription Results Dialog */}
      <CustomDialog
        open={openResultsDialog}
        title="Link Devices Results"
        description={
          <>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", marginBottom: 2 }}
            >
              Device subscription process completed. Here are the results:
            </Typography>

            {subscriptionResults.s3Url && (
              <Button
                variant="contained"
                sx={{ mb: 2 }}
                onClick={() => window.open(subscriptionResults.s3Url, "_blank")}
              >
                Download Results Excel
              </Button>
            )}

            {/* Total Users */}
            {subscriptionResults.totalUsers?.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, color: "primary.main" }}
                >
                  Total Users ({subscriptionResults.totalUsers.length})
                </Typography>
                <DataGrid
                  columns={[
                    {
                      field: "firstName",
                      headerName: "First Name",
                      width: 100,
                    },
                    { field: "lastName", headerName: "Last Name", width: 100 },
                    {
                      field: "contactNumber",
                      headerName: "Contact",
                      width: 120,
                    },
                    {
                      field: "deviceCode",
                      headerName: "Device Code",
                      width: 120,
                    },
                    {
                      field: "subscriptionStatus",
                      headerName: "Status",
                      width: 150,
                    },
                  ]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  rows={subscriptionResults.totalUsers.map((user, index) => ({
                    ...user,
                    id: index,
                  }))}
                  sx={{ width: "100%", mb: 2 }}
                  getRowId={(row) => row?.id}
                  disableRowSelectionOnClick
                  filterMode="client"
                  pagination
                  pageSizeOptions={[5, 10, 25]}
                  paginationMode="client"
                  sortingMode="client"
                  autoHeight
                  slots={{ toolbar: GridToolbarExport }}
                />
              </>
            )}

            {/* New Users */}
            {subscriptionResults.newUsers?.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, color: "success.main" }}
                >
                  New Users ({subscriptionResults.newUsers.length})
                </Typography>
                <DataGrid
                  columns={[
                    {
                      field: "firstName",
                      headerName: "First Name",
                      width: 100,
                    },
                    { field: "lastName", headerName: "Last Name", width: 100 },
                    {
                      field: "contactNumber",
                      headerName: "Contact",
                      width: 120,
                    },
                    {
                      field: "deviceCode",
                      headerName: "Device Code",
                      width: 120,
                    },
                  ]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  rows={subscriptionResults.newUsers.map((user, index) => ({
                    ...user,
                    id: index,
                  }))}
                  sx={{ width: "100%", mb: 2 }}
                  getRowId={(row) => row?.id}
                  disableRowSelectionOnClick
                  filterMode="client"
                  pagination
                  pageSizeOptions={[5, 10, 25]}
                  paginationMode="client"
                  sortingMode="client"
                  autoHeight
                  slots={{ toolbar: GridToolbarExport }}
                />
              </>
            )}

            {/* Users with Device (Code Verified True) */}
            {subscriptionResults.usersWithIsCodeVerifiedTrue?.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, color: "info.main" }}
                >
                  Users with Device (
                  {subscriptionResults.usersWithIsCodeVerifiedTrue.length})
                </Typography>
                <DataGrid
                  columns={[
                    {
                      field: "firstName",
                      headerName: "First Name",
                      width: 100,
                    },
                    { field: "lastName", headerName: "Last Name", width: 100 },
                    {
                      field: "contactNumber",
                      headerName: "Contact",
                      width: 120,
                    },
                    {
                      field: "deviceCode",
                      headerName: "Device Code",
                      width: 120,
                    },
                  ]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  rows={subscriptionResults.usersWithIsCodeVerifiedTrue.map(
                    (user, index) => ({
                      ...user,
                      id: index,
                    }),
                  )}
                  sx={{ width: "100%", mb: 2 }}
                  getRowId={(row) => row?.id}
                  disableRowSelectionOnClick
                  filterMode="client"
                  pagination
                  pageSizeOptions={[5, 10, 25]}
                  paginationMode="client"
                  sortingMode="client"
                  autoHeight
                  slots={{ toolbar: GridToolbarExport }}
                />
              </>
            )}

            {/* Users without Device (Code Verified False) */}
            {subscriptionResults.usersWithIsCodeVerifiedFalse?.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ mt: 2, mb: 1, color: "warning.main" }}
                >
                  Users without Device (
                  {subscriptionResults.usersWithIsCodeVerifiedFalse.length})
                </Typography>
                <DataGrid
                  columns={[
                    {
                      field: "firstName",
                      headerName: "First Name",
                      width: 100,
                    },
                    { field: "lastName", headerName: "Last Name", width: 100 },
                    {
                      field: "contactNumber",
                      headerName: "Contact",
                      width: 120,
                    },
                    { field: "email", headerName: "Email", width: 150 },
                  ]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  rows={subscriptionResults.usersWithIsCodeVerifiedFalse.map(
                    (user, index) => ({
                      ...user,
                      id: index,
                    }),
                  )}
                  sx={{ width: "100%", mb: 2 }}
                  getRowId={(row) => row?.id}
                  disableRowSelectionOnClick
                  filterMode="client"
                  pagination
                  pageSizeOptions={[5, 10, 25]}
                  paginationMode="client"
                  sortingMode="client"
                  autoHeight
                  slots={{ toolbar: GridToolbarExport }}
                />
              </>
            )}

            {/* Summary if no data */}
            {subscriptionResults.totalUsers?.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No results available. Please download the Excel file for more
                information.
              </Typography>
            )}
          </>
        }
        cancelButton={false}
        confirmText="Close"
        onConfirm={() => {
          setOpenResultsDialog(false);
          setSubscriptionResults({
            newUsers: [],
            usersWithIsCodeVerifiedFalse: [],
            usersWithIsCodeVerifiedTrue: [],
            totalUsers: [],
            s3Url: "",
          });
          onClose();
        }}
        isLoading={loading}
      />
    </>
  );
}
